import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { type GridRowParams } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { DjangoPaginationAdapter } from "api/common/adapters/pagination.adapters";
import { TableQueryParams } from "api/common/adapters/pagination.types";
import { createQueryAdapter } from "api/common/adapters/queryParam.adapter";
import { useListMailUsersQuery } from "api/memberPortal/mailList/mailList.api";
import SearchBar from "components/MUI/DataGrid/Detached/SearchBar";
import BackButton from "components/UI/BackButton/BackButton";

import MailListMembersDrawer from "./components/MailListMemberDetails/MailListMembers.drawer";
import MailListMembersTable from "./components/MailListMembers.table";

const memberApiAdapter = createQueryAdapter({
  paginationAdapter: DjangoPaginationAdapter,
});
export default function MailListMembersScreen() {
  const navigate = useNavigate();
  const [tableParams, setTableParams] = useState<TableQueryParams>({
    page: 0,
    pageSize: 100,
    sort: { field: "createdOn", direction: "desc" },
    search: "",
  });

  const [selectedMemberId, setSelectedMemberId] = useState<number | undefined>(
    undefined,
  );

  const memberQueryParams = useMemo(() => {
    return memberApiAdapter.toTableParams({
      pagination: tableParams,
    });
  }, [tableParams]);

  const { data: members, isLoading } = useListMailUsersQuery(memberQueryParams);
  const { selectedMember } = useListMailUsersQuery(memberQueryParams, {
    selectFromResult: ({ data }) => ({
      selectedMember: data?.results.find((m) => m.id === selectedMemberId),
    }),
  });

  const handleSearchChange = useCallback((value: string) => {
    setTableParams((prev) => ({ ...prev, search: value, page: 0 }));
  }, []);

  if (!members && !isLoading)
    return <Alert severity="error">Failed to load members</Alert>;

  const handleMemberSelectChange = (row: GridRowParams) => {
    setSelectedMemberId(row.row.id);
  };

  const handleOpenCreateMember = () => {
    navigate("add");
  };

  return (
    <Box>
      <BackButton to=".." label="Mailing Lists" />
      <Stack sx={{ my: 5, mx: 10 }} spacing={2}>
        <Typography variant="h2">Members</Typography>
        <Stack direction="row" spacing={2}>
          <SearchBar
            sx={{ flex: 1, maxWidth: 500 }}
            onSearchChange={handleSearchChange}
          />
          <Button
            startIcon={<PersonAddAltIcon />}
            variant="contained"
            onClick={handleOpenCreateMember}
          >
            Add Member
          </Button>
        </Stack>
      </Stack>
      <Stack direction="row" gap={3} sx={{ mx: 10 }}>
        <Box sx={{ width: 500, flex: 1 }}>
          <MailListMembersTable
            memberData={members ?? { count: 0, results: [] }}
            isLoading={isLoading}
            tableParams={tableParams}
            onTableParamsChange={setTableParams}
            onMemberSelectChange={handleMemberSelectChange}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <MailListMembersDrawer
            key={selectedMemberId ?? "empty"}
            member={selectedMember}
          />
        </Box>
      </Stack>
      <Outlet />
    </Box>
  );
}
